import { toast } from "sonner";
import { useMemo, useState } from "react";
import { computeDocTotals, fmtPrice } from "@/lib/totals";
import { Link } from "react-router-dom";
import { Save, Search } from "lucide-react";
import { KIND_LABEL, MAX_LINES, formatDateTime } from "./constants";
import GroupedItemsView from "./GroupedItemsView";
import AddRepairButton from "./AddRepairButton";
import InventorySearchDialog from "./InventorySearchDialog";
import UnsavedDot from "@/components/UnsavedDot";

/**
 * Vista de detalle/edición de un documento de Facturación
 * (factura, albarán o abono).
 *
 * Estructura:
 *   1. Cabecera   — tipo + código + fechas + relaciones (factura origen / repair)
 *   2. Cliente    — datos fiscales del `customer_tax_snapshot`
 *   3. Items      — `GroupedItemsView` agrupado por reparación + AddRepairButton + totales calculados en vivo
 *   4. Pago/notas — observaciones (PDF), forma de pago, nota interna (no-PDF)
 *   5. Botón Guardar
 *
 * Totales: se asume `prices_include_vat=true` (iter 46 en adelante).
 * Para abonos (`kind="credit_note"`) los totales se renderizan negados (`sign=-1`).
 *
 * Extraído de `pages/Billing.jsx` en el refactor preventivo de iteración 66.
 * Los `data-testid` y la lógica son idénticos a la versión inline previa.
 */
export default function DocView({
  doc, items, setItems, parts,
  obs, setObs, internal, setInternal,
  pm, setPm, payMethods,
  repairLookup = {}, onSave, isDirty = false,
}) {
  const tax = doc.customer_tax_snapshot || {};
  const sign = doc.kind === "credit_note" ? -1 : 1;
  // Iter 117 — Buscador de inventario en modal.
  const [showInvSearch, setShowInvSearch] = useState(false);

  // computed totals (asume prices_include_vat=True por defecto a partir de iter 46)
  const computed = useMemo(() => {
    // Iter 168 — fórmula única (lib/totals.js ≡ services/totals.py). Usa el
    // line_total guardado del documento, así los históricos se muestran tal cual.
    const { subtotal: sub, tax: t, total: tot } = computeDocTotals(items, true);
    const avg = sub ? (t / sub * 100).toFixed(2) : 0;
    return { subtotal: sub, tax: t, total: tot, tax_rate: avg };
  }, [items]);

  return (
    <div className="ds-card shadow-hard-sm">
      {/* Doc header */}
      <div className="p-6 border-b border-[#E5E5E0] flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#6E8B3D]">
            {KIND_LABEL[doc.kind]}
          </div>
          <div className="font-display text-3xl font-black mt-1">{doc.code}</div>
          {doc.source_invoice_code && (
            <div className="text-xs text-[#525252] mt-1">
              Abono de la factura{" "}
              <Link to={`/billing?doc=${doc.source_invoice_id}`} className="text-[#6E8B3D] hover:underline">
                {doc.source_invoice_code}
              </Link>
            </div>
          )}
          {doc.repair_code && (
            <div className="text-xs text-[#525252] mt-0.5">
              Reparación{" "}
              <Link to={`/repairs/${doc.repair_id}`} className="text-[#6E8B3D] hover:underline">
                {doc.repair_code}
              </Link>
            </div>
          )}
        </div>
        <div className="text-right text-xs text-[#525252]">
          <div>Fecha: <span className="font-mono">{formatDateTime(doc.created_at)}</span></div>
          <div>Creado por: <span className="font-mono">{doc.created_by || "—"}</span></div>
        </div>
      </div>

      {/* Customer */}
      <div className="p-6 border-b border-[#E5E5E0]">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
          Cliente
        </div>
        <div className="text-sm space-y-0.5">
          <div className="font-semibold text-base">{tax.name || doc.customer_name}</div>
          {tax.tax_id && <div className="font-mono text-xs text-[#525252]">{tax.tax_id}</div>}
          {tax.address && <div className="text-xs text-[#525252]">{tax.address}</div>}
          {(tax.postal_code || tax.city) && (
            <div className="text-xs text-[#525252]">
              {tax.postal_code} {tax.city}
            </div>
          )}
          {(tax.province || tax.country) && (
            <div className="text-xs text-[#525252]">{tax.province} {tax.country}</div>
          )}
        </div>
      </div>

      {/* Items - agrupados por reparación */}
      <div className="p-6 border-b border-[#E5E5E0]">
        <GroupedItemsView
          items={items}
          setItems={setItems}
          parts={parts}
          repairLookup={repairLookup}
          sign={sign}
          docRepairId={doc.repair_id || ""}
        />
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <AddRepairButton
            existingRepairIds={Array.from(new Set(items.map((x) => x.repair_id_ref).filter(Boolean)))}
            onPick={async (repair) => {
              // Iter 131 — Al añadir una reparación al documento, importar
              // AUTOMÁTICAMENTE todas sus líneas de Productos/Servicios,
              // conservando precio, descuento, garantía, IVA, cantidad y
              // tipo tal cual están guardadas en la reparación.
              //
              // Si la reparación ya está en el documento, avisar y salir.
              const alreadyLinked = items.some(
                (it) => (it.repair_id_ref || "") === repair.id,
              );
              if (alreadyLinked) {
                toast.info(`La reparación ${repair.code} ya está añadida.`);
                return;
              }
              // Refrescar el lookup local para que la cabecera del grupo
              // aparezca inmediatamente en GroupedItemsView.
              const next = { ...repairLookup };
              next[repair.id] = {
                code: repair.code,
                device_type: repair.device_type || "",
                model: repair.model || "",
                serial: repair.serial || "",
              };
              Object.assign(repairLookup, next);

              // `GET /repairs?q=...` ya trae `items` en cada reparación.
              // Reusamos ese payload; solo si viniese vacío, hacemos un
              // GET /repairs/{id} adicional como fallback.
              let repairItems = Array.isArray(repair.items) ? repair.items : [];
              if (repairItems.length === 0) {
                try {
                  const { default: api } = await import("@/lib/api");
                  const r = await api.get(`/repairs/${repair.id}`);
                  repairItems = Array.isArray(r.data?.items) ? r.data.items : [];
                } catch { /* noop */ }
              }

              if (repairItems.length === 0) {
                // Reparación sin líneas: añadir un concepto vacío para que
                // el usuario pueda escribir manualmente lo que quiera.
                setItems([...items, {
                  product_id: null, sku: "", name: "", description: "",
                  kind: "product",
                  repair_id_ref: repair.id,
                  quantity: 1, unit_price: 0, vat_rate: 21,
                  discount_pct: 0, warranty_months: 0,
                }]);
                return;
              }

              // Verificar el límite máximo de líneas del documento
              // (`MAX_LINES` = 20 según billing constants).
              const room = MAX_LINES - items.length;
              const toImport = repairItems.slice(0, Math.max(0, room));

              const newLines = toImport.map((it) => ({
                product_id: it.product_id ?? null,
                sku: it.sku || "",
                name: it.name || "",
                description: it.description || "",
                kind: it.kind || "product",
                repair_id_ref: repair.id,
                quantity: Number(it.quantity) || 1,
                unit_price: Number(it.unit_price) || 0,
                discount_pct: Number(it.discount_pct) || 0,
                vat_rate: Number(it.vat_rate) || 21,
                warranty_months: Number(it.warranty_months) || 0,
                // `stock_consumed` no se propaga a la línea del documento:
                // el control de stock se gestiona A NIVEL DE REPARACIÓN
                // (services.inventory._consume_repair_stock usa la flag de
                // la reparación) → NO hay doble descuento al importar.
              }));

              setItems([...items, ...newLines]);

              if (toImport.length < repairItems.length) {
                toast.warning(
                  `Se han importado ${toImport.length} de ${repairItems.length} líneas (límite ${MAX_LINES}).`,
                );
              } else {
                toast.success(
                  `Reparación ${repair.code} añadida con ${toImport.length} línea${toImport.length === 1 ? "" : "s"}.`,
                );
              }
            }}
          />
          {/* Iter 117 — Buscador de inventario (lupa) para añadir múltiples artículos. */}
          <button
            type="button"
            onClick={() => setShowInvSearch(true)}
            data-testid="docview-open-inventory-search"
            className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[#111110] hover:bg-[#111110] hover:text-white text-xs font-semibold transition-colors"
            title="Buscar y añadir artículos del inventario"
          >
            <Search size={12} /> Añadir del inventario
          </button>
          <div className="text-[10px] font-mono text-[#525252]">
            {items.length} línea{items.length !== 1 ? "s" : ""} · {items.length}/{MAX_LINES}
          </div>
        </div>

        {/* Totals (live computed) */}
        <div className="mt-4 flex justify-end">
          <table className="text-xs">
            <tbody>
              <tr>
                <td className="py-1 pr-6 text-[#525252] font-mono">Base imponible</td>
                <td className="py-1 text-right font-mono">{(computed.subtotal * sign).toFixed(2).replace(".", ",")} €</td>
              </tr>
              <tr>
                <td className="py-1 pr-6 text-[#525252] font-mono">IVA ({computed.tax_rate}%)</td>
                <td className="py-1 text-right font-mono">{(computed.tax * sign).toFixed(2).replace(".", ",")} €</td>
              </tr>
              <tr className="border-t-2 border-[#111110]">
                <td className="pt-1.5 pr-6 font-bold">Total {KIND_LABEL[doc.kind].toLowerCase()}</td>
                <td className="pt-1.5 text-right font-mono font-bold text-base">
                  {fmtPrice(computed.total * sign)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Observations + payment + internal */}
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            Observaciones (visible en PDF)
          </label>
          <textarea
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            rows={3}
            data-testid="billing-observations"
            className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
          />
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mt-3 block">
            Forma de pago
          </label>
          <select
            value={pm}
            onChange={(e) => setPm(e.target.value)}
            data-testid="billing-payment"
            className="mt-1 w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
          >
            {payMethods.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-amber-700">
            Nota interna · NO aparece en PDF
          </label>
          <textarea
            value={internal}
            onChange={(e) => setInternal(e.target.value)}
            rows={5}
            data-testid="billing-internal"
            className="mt-1 w-full bg-amber-50 border border-amber-300 focus:border-amber-500 outline-none px-3 py-2 text-sm rounded-none"
          />
        </div>
      </div>

      <div className="px-6 pb-6">
        <button
          onClick={onSave}
          data-testid="billing-save"
          className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-sm font-semibold"
        >
          <UnsavedDot show={isDirty} />
          <Save size={14} /> Guardar cambios
        </button>
      </div>
      <InventorySearchDialog
        open={showInvSearch}
        onClose={() => setShowInvSearch(false)}
        parts={parts}
        onAdd={(picked) => {
          // Iter 117 — Añadimos los ítems seleccionados al final. Si el
          // documento tiene una reparación asociada (`doc.repair_id`), las
          // nuevas líneas se enlazan a ese grupo; en caso contrario van al
          // grupo "Sin reparación".
          const repRef = doc.repair_id || "";
          const enriched = picked.map((p) => ({ ...p, repair_id_ref: repRef }));
          setItems([...items, ...enriched]);
        }}
      />
    </div>
  );
}
